import { useTranslation } from 'react-i18next'

import {
  TCompareField,
  TCompareStatus,
  TNotComparedReason,
  TNotScreenedReason,
  TScreeningCriterion,
  TSubmittedField,
} from '../model/types'

export interface IReviewText {
  key: string
  fallback: string
}

const criterion: Record<TScreeningCriterion, IReviewText> = {
  partnerId: {
    key: 'onb-competitor-bad-actor-review-form--criterion-partner-id',
    fallback: 'Identity match on partner ID',
  },
  taxId: {
    key: 'onb-competitor-bad-actor-review-form--criterion-tax-id',
    fallback: 'Identity match on tax ID',
  },
  website: {
    key: 'onb-competitor-bad-actor-review-form--criterion-website',
    fallback: 'Identity match on website',
  },
  email: {
    key: 'onb-competitor-bad-actor-review-form--criterion-email',
    fallback: 'Identity match on email address',
  },
  emailDomain: {
    key: 'onb-competitor-bad-actor-review-form--criterion-email-domain',
    fallback: 'Email domain',
  },
  exactName: {
    key: 'onb-competitor-bad-actor-review-form--criterion-exact-name',
    fallback: 'Exact name',
  },
  wholeWordName: {
    key: 'onb-competitor-bad-actor-review-form--criterion-whole-word-name',
    fallback: 'Whole-word name',
  },
  similarity: {
    key: 'onb-competitor-bad-actor-review-form--criterion-similarity',
    fallback: 'Name similarity',
  },
}

const compareField: Record<TCompareField, IReviewText> = {
  name: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-name',
    fallback: 'Company name',
  },
  partnerId: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-partner-id',
    fallback: 'Partner ID',
  },
  taxId: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-tax-id',
    fallback: 'Tax ID',
  },
  website: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-website',
    fallback: 'Website',
  },
  requestorEmail: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-requestor-email',
    fallback: 'Requestor email',
  },
  generalManagerEmail: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-general-manager-email',
    fallback: 'General manager email',
  },
  companyEmail: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-company-email',
    fallback: 'Company email',
  },
  address: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-address',
    fallback: 'Address',
  },
  city: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-city',
    fallback: 'City',
  },
  postalCode: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-postal-code',
    fallback: 'Postal code',
  },
  state: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-state',
    fallback: 'State',
  },
  country: {
    key: 'onb-competitor-bad-actor-review-form--compare-field-country',
    fallback: 'Country',
  },
}

const submittedField: Record<TSubmittedField, IReviewText> = {
  legalName: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-legal-name',
    fallback: 'Legal name',
  },
  tradingName: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-trading-name',
    fallback: 'Trading name',
  },
  country: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-country',
    fallback: 'Country',
  },
  address: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-address',
    fallback: 'Address',
  },
  city: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-city',
    fallback: 'City',
  },
  postalCode: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-postal-code',
    fallback: 'Postal code',
  },
  state: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-state',
    fallback: 'State',
  },
  taxId: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-tax-id',
    fallback: 'Tax ID',
  },
  website: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-website',
    fallback: 'Website',
  },
  requestorEmail: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-requestor-email',
    fallback: 'Requestor email',
  },
  generalManagerEmail: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-general-manager-email',
    fallback: 'General manager email',
  },
  companyEmail: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-company-email',
    fallback: 'Company email',
  },
  partnerId: {
    key: 'onb-competitor-bad-actor-review-form--submitted-field-partner-id',
    fallback: 'Partner ID',
  },
}

const compareStatus: Record<TCompareStatus, IReviewText> = {
  matched: {
    key: 'onb-competitor-bad-actor-review-form--status-matched',
    fallback: 'Matched',
  },
  similar: {
    key: 'onb-competitor-bad-actor-review-form--status-similar',
    fallback: 'Similar',
  },
  different: {
    key: 'onb-competitor-bad-actor-review-form--status-different',
    fallback: 'Different',
  },
  'not-compared': {
    key: 'onb-competitor-bad-actor-review-form--status-not-compared',
    fallback: 'Not compared',
  },
}

const notComparedReason: Record<TNotComparedReason, IReviewText> = {
  'no-submitted-value': {
    key: 'onb-competitor-bad-actor-review-form--not-compared-no-submitted-value',
    fallback: 'not given in the registration',
  },
  'no-listed-value': {
    key: 'onb-competitor-bad-actor-review-form--not-compared-no-listed-value',
    fallback: 'no value in the list entry',
  },
  'no-values': {
    key: 'onb-competitor-bad-actor-review-form--not-compared-no-values',
    fallback: 'no value on either side',
  },
  'submitted-unusable': {
    key: 'onb-competitor-bad-actor-review-form--not-compared-submitted-unusable',
    fallback: 'the registration value is a placeholder or incomplete',
  },
  'listed-unusable': {
    key: 'onb-competitor-bad-actor-review-form--not-compared-listed-unusable',
    fallback: 'the list entry value is a placeholder or incomplete',
  },
  'name-not-compared': {
    key: 'onb-competitor-bad-actor-review-form--not-compared-name-not-compared',
    fallback: 'compared only together with the company name',
  },
}

const notScreenedReason: Record<TNotScreenedReason, IReviewText> = {
  'no-submitted-registration': {
    key: 'onb-competitor-bad-actor-review-form--not-screened-no-submitted-registration',
    fallback: 'This request has no submitted registration to screen.',
  },
  'no-region': {
    key: 'onb-competitor-bad-actor-review-form--not-screened-no-region',
    fallback: "The registration's country has no screening region, so it cannot be screened.",
  },
  'no-list-for-region': {
    key: 'onb-competitor-bad-actor-review-form--not-screened-no-list-for-region',
    fallback: 'There is no list for this region.',
  },
  'no-company-value': {
    key: 'onb-competitor-bad-actor-review-form--not-screened-no-company-value',
    fallback: 'The registration gives no company name and no company identifier to compare.',
  },
  'list-empty': {
    key: 'onb-competitor-bad-actor-review-form--not-screened-list-empty',
    fallback: 'The list has no valid entries, so nothing was compared.',
  },
  'list-holds-none-of-the-values': {
    key: 'onb-competitor-bad-actor-review-form--not-screened-list-holds-none-of-the-values',
    fallback: 'The list holds none of the company values this registration gives.',
  },
}

export const COMPETITOR_BAD_ACTOR_REVIEW_TEXT = {
  screeningMissing: {
    key: 'onb-competitor-bad-actor-review-form--screening-missing',
    fallback:
      'This step\'s screening form has no screening set. Expected "competitors" or "bad-actors". Ask the template owner to fix the form settings on this step.',
  },
  screeningUnknown: {
    key: 'onb-competitor-bad-actor-review-form--screening-unknown',
    fallback:
      'This step\'s screening form is set to {{value}}. Expected "competitors" or "bad-actors". Ask the template owner to fix the form settings on this step.',
  },
  requestInvalid: {
    key: 'onb-competitor-bad-actor-review-form--request-invalid',
    fallback: 'This form cannot load the screening: request number {{value}} is not valid.',
  },
  headingCompetitors: {
    key: 'onb-competitor-bad-actor-review-form--heading-competitors',
    fallback: 'Competitor screening',
  },
  headingBadActors: {
    key: 'onb-competitor-bad-actor-review-form--heading-bad-actors',
    fallback: 'Bad-actors screening',
  },
  resultLoading: {
    key: 'onb-competitor-bad-actor-review-form--result-loading',
    fallback: 'Loading the screening result…',
  },
  resultError: {
    key: 'onb-competitor-bad-actor-review-form--result-error',
    fallback: 'Could not load the screening result.',
  },
  retry: {
    key: 'onb-competitor-bad-actor-review-form--retry',
    fallback: 'Retry',
  },
  resultNone: {
    key: 'onb-competitor-bad-actor-review-form--result-none',
    fallback: 'No screening result is stored on this request.',
  },
  refused: {
    key: 'onb-competitor-bad-actor-review-form--refused',
    fallback: 'You are not entitled to see this screening.',
  },
  tierIdentity: {
    key: 'onb-competitor-bad-actor-review-form--tier-identity',
    fallback: 'Identity match',
  },
  tierPossible: {
    key: 'onb-competitor-bad-actor-review-form--tier-possible',
    fallback: 'Possible match',
  },
  tierNone: {
    key: 'onb-competitor-bad-actor-review-form--tier-none',
    fallback: 'No match',
  },
  outcome: {
    key: 'onb-competitor-bad-actor-review-form--outcome',
    fallback: 'Outcome: {{tier}}',
  },
  resultMatchedCount: {
    key: 'onb-competitor-bad-actor-review-form--result-matched-count',
    fallback: 'Matched entries: {{total}}.',
  },
  resultShowing: {
    key: 'onb-competitor-bad-actor-review-form--result-showing',
    fallback: 'Matched entries shown: {{shown}} of {{total}}.',
  },
  matchedRegionLabel: {
    key: 'onb-competitor-bad-actor-review-form--matched-region-label',
    fallback: 'Matched list entries',
  },
  matchedCaption: {
    key: 'onb-competitor-bad-actor-review-form--matched-caption',
    fallback: 'Matched list entries.',
  },
  compareInstruction: {
    key: 'onb-competitor-bad-actor-review-form--compare-instruction',
    fallback: 'Select an entry to compare it with the registration.',
  },
  colListedName: {
    key: 'onb-competitor-bad-actor-review-form--col-listed-name',
    fallback: 'Listed name',
  },
  colHow: {
    key: 'onb-competitor-bad-actor-review-form--col-how',
    fallback: 'Matched on',
  },
  colScore: {
    key: 'onb-competitor-bad-actor-review-form--col-score',
    fallback: 'Score',
  },
  criterion,
  listedTimes: {
    key: 'onb-competitor-bad-actor-review-form--listed-times',
    fallback: 'listed {{count}} times',
  },
  scoreValue: {
    key: 'onb-competitor-bad-actor-review-form--score-value',
    fallback: '{{score}}%',
  },
  scoreHigh: {
    key: 'onb-competitor-bad-actor-review-form--score-high',
    fallback: 'at or above the escalation boundary',
  },
  noValue: {
    key: 'onb-competitor-bad-actor-review-form--no-value',
    fallback: '—',
  },
  rerunHint: {
    key: 'onb-competitor-bad-actor-review-form--rerun-hint',
    fallback: "Run this step's screening again on the registration as it is now.",
  },
  rerunButton: {
    key: 'onb-competitor-bad-actor-review-form--rerun-button',
    fallback: 'Re-run screening',
  },
  rerunTitleCompetitors: {
    key: 'onb-competitor-bad-actor-review-form--rerun-title-competitors',
    fallback: 'Competitor screening, run again now',
  },
  rerunTitleBadActors: {
    key: 'onb-competitor-bad-actor-review-form--rerun-title-bad-actors',
    fallback: 'Bad-actors screening, run again now',
  },
  rerunLoading: {
    key: 'onb-competitor-bad-actor-review-form--rerun-loading',
    fallback: 'Running the screening on the registration…',
  },
  rerunError: {
    key: 'onb-competitor-bad-actor-review-form--rerun-error',
    fallback: 'Could not run the screening.',
  },
  rerunNotFound: {
    key: 'onb-competitor-bad-actor-review-form--rerun-not-found',
    fallback: 'This request was not found.',
  },
  rerunNoMatch: {
    key: 'onb-competitor-bad-actor-review-form--rerun-no-match',
    fallback: 'No list entry matches this registration.',
  },
  rerunRegionLabel: {
    key: 'onb-competitor-bad-actor-review-form--rerun-region-label',
    fallback: 'List entries matching the registration',
  },
  rerunCountOne: {
    key: 'onb-competitor-bad-actor-review-form--rerun-count-one',
    fallback: '{{tier}}: 1 list entry matches the registration now.',
  },
  rerunCountOther: {
    key: 'onb-competitor-bad-actor-review-form--rerun-count-other',
    fallback: '{{tier}}: {{count}} list entries match the registration now.',
  },
  rerunSubmittedHeading: {
    key: 'onb-competitor-bad-actor-review-form--rerun-submitted-heading',
    fallback: 'Registration values compared',
  },
  notScreenedUnknown: {
    key: 'onb-competitor-bad-actor-review-form--not-screened-unknown',
    fallback: 'The screening could not run on this registration.',
  },
  compareLabel: {
    key: 'onb-competitor-bad-actor-review-form--compare-label',
    fallback: '{{listedName}} compared with the registration',
  },
  compareColField: {
    key: 'onb-competitor-bad-actor-review-form--compare-col-field',
    fallback: 'Field',
  },
  compareColListed: {
    key: 'onb-competitor-bad-actor-review-form--compare-col-listed',
    fallback: 'List entry value',
  },
  compareColSubmitted: {
    key: 'onb-competitor-bad-actor-review-form--compare-col-submitted',
    fallback: 'Registration value',
  },
  compareColStatus: {
    key: 'onb-competitor-bad-actor-review-form--compare-col-status',
    fallback: 'Status',
  },
  compareStoredNote: {
    key: 'onb-competitor-bad-actor-review-form--compare-stored-note',
    fallback:
      'Only the fields the screening compared were stored. Re-run the screening to compare every field.',
  },
  compareAlsoListedAs: {
    key: 'onb-competitor-bad-actor-review-form--compare-also-listed-as',
    fallback: 'Also listed as: {{names}}',
  },
  compareAlternativeName: {
    key: 'onb-competitor-bad-actor-review-form--compare-alternative-name',
    fallback: 'alternative name',
  },
  compareLegalName: {
    key: 'onb-competitor-bad-actor-review-form--compare-legal-name',
    fallback: 'legal name',
  },
  compareTradingName: {
    key: 'onb-competitor-bad-actor-review-form--compare-trading-name',
    fallback: 'trading name',
  },
  compareField,
  submittedField,
  compareStatus,
  notComparedReason,
  notScreenedReason,
}

export const useReviewText = () => {
  const { t } = useTranslation()

  return (text: IReviewText, values: Record<string, string | number> = {}): string =>
    t(text.key, { defaultValue: text.fallback, ...values })
}

const isScreeningCriterion = (value: string): value is TScreeningCriterion =>
  Object.prototype.hasOwnProperty.call(criterion, value)

export const criterionText = (value: string): IReviewText | null =>
  isScreeningCriterion(value) ? criterion[value] : null

const isCompareField = (value: string): value is TCompareField =>
  Object.prototype.hasOwnProperty.call(compareField, value)

export const compareFieldText = (value: string): IReviewText | null =>
  isCompareField(value) ? compareField[value] : null

const isCompareStatus = (value: string): value is TCompareStatus =>
  Object.prototype.hasOwnProperty.call(compareStatus, value)

export const compareStatusText = (value: string): IReviewText | null =>
  isCompareStatus(value) ? compareStatus[value] : null

const isNotComparedReason = (value: string): value is TNotComparedReason =>
  Object.prototype.hasOwnProperty.call(notComparedReason, value)

export const notComparedReasonText = (value: string): IReviewText | null =>
  isNotComparedReason(value) ? notComparedReason[value] : null

const isNotScreenedReason = (value: string): value is TNotScreenedReason =>
  Object.prototype.hasOwnProperty.call(notScreenedReason, value)

export const notScreenedReasonText = (value: string): IReviewText =>
  isNotScreenedReason(value)
    ? notScreenedReason[value]
    : COMPETITOR_BAD_ACTOR_REVIEW_TEXT.notScreenedUnknown

export const tierText = (tier: string): IReviewText | null => {
  switch (tier) {
    case 'IdentityMatch':
      return COMPETITOR_BAD_ACTOR_REVIEW_TEXT.tierIdentity
    case 'PossibleMatch':
      return COMPETITOR_BAD_ACTOR_REVIEW_TEXT.tierPossible
    case 'NoMatch':
      return COMPETITOR_BAD_ACTOR_REVIEW_TEXT.tierNone
    default:
      return null
  }
}
